import { Instance, Customer, Invoice, LoginMetrics, TransactionsByTypeEntry } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const QB_API_BASE = `${API_BASE_URL}/qbdconnect/v1`;

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

// Helper function to make authenticated requests
async function authenticatedFetch(url: string, options: RequestInit = {}) {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token available');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    // Token expired or invalid - clear auth and redirect to login
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    window.location.href = '/login';
    throw new Error('Authentication expired. Please log in again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || `Request failed: ${response.statusText}`);
  }

  return response;
}

export const api = {
  // Get a single instance by ID
  async getCustomerById(customerId: string): Promise<Customer> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/customers/${customerId}`);
      const customer = await response.json();

      return customer; 
    } catch (error) {
      console.error('Error fetching customer:', error);
      throw error;
    }
  },

  // Get all instances
  async getInstances(customerId: string): Promise<Instance[]> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/instances?customerId=${customerId}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching instances:', error);
      throw error;
    }
  },

  // Get instances grouped by customer
  async getInstancesByCustomer(customers: Customer[]): Promise<Customer[]> {
    try {
      const customersMap = await Promise.all(
        customers.map(async (customer) => {
          // Fetch instances
          const instances = await this.getInstances(customer.id);
          return { ...customer, instances }
        })
      );

      // Group instances by customer
     /* 
      customers.forEach(customer => {
        customersMap.set(customer.id, {
          ...customer,
          instances: [],
        });
      });
      instances.forEach(instance => {
        if (instance.customerId && customersMap.has(instance.customerId)) {
          const customer = customersMap.get(instance.customerId)!;
          customer.instances.push(instance);
        }
      });
      */
      /*
      const returnArray = Array.from(customersMap.values()).filter(
        customer => customer.instances.length > 0
      );
      */

      // Convert map to array and filter out customers with no instances
      return customersMap.filter(
        customer => customer.instances.length > 0
      );
    } catch (error) {
      console.error('Error fetching instances by customer:', error);
      throw error;
    }
  },

  // Get a single instance by ID
  async getInstanceById(instanceId: string): Promise<Instance> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/instances/${instanceId}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching instance:', error);
      throw error;
    }
  },

  // Update instance
  async updateInstance(instanceId: string, data: Partial<Instance>): Promise<Instance> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/instances/${instanceId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      console.error('Error updating instance:', error);
      throw error;
    }
  },

  // Delete instance
  async deleteInstance(instanceId: string): Promise<void> {
    try {
      await authenticatedFetch(`${QB_API_BASE}/instances/${instanceId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting instance:', error);
      throw error;
    }
  },

  // Update user
  async updateUser(userId: string, data: { firstName: string; lastName: string; email: string; phone: string }): Promise<any> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  // Get user profile
  async getUserProfile(): Promise<any> {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/user/profile`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  // Get dashboard data
  async getDashboardData(): Promise<any> {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/dashboard`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  },

  // Send QWC file via email for an instance
  async sendQwcFile(instanceId: string): Promise<{ message: string }> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/instances/${instanceId}/qwc`);
      return await response.json();
    } catch (error) {
      console.error('Error sending QWC file:', error);
      throw error;
    }
  },

  // Test connection for an instance
  async testConnection(instanceId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await authenticatedFetch(
        `${QB_API_BASE}/instances/${instanceId}/test-connection`,
        { method: 'POST' }
      );
      return await response.json();
    } catch (error) {
      console.error('Error testing connection:', error);
      throw error;
    }
  },

  // Search customers by partial text
  async searchCustomers(searchText: string): Promise<Customer[]> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/customers?search=${encodeURIComponent(searchText)}`);
      return await response.json();
    } catch (error) {
      console.error('Error searching customers:', error);
      throw error;
    }
  },

  // Add user to existing customer
  async addUserToCustomer(userId: string, customerId: string): Promise<void> {
    try {
      await authenticatedFetch(`${QB_API_BASE}/users/${userId}/customers`, {
        method: 'POST',
        body: JSON.stringify({ customerId }),
      });
    } catch (error) {
      console.error('Error adding user to customer:', error);
      throw error;
    }
  },

  // Create new customer
  async createCustomer(data: {
    name: string;
    address: {
      street1: string;
      street2?: string;
      city: string;
      state: string;
      postalCode: string;
    };
  }): Promise<Customer> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/customers`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  },

  // Get all products
  async getProducts(): Promise<any[]> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/products`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  // Get product by ID (for checkout page)
  async getProductById(productId: string): Promise<any> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/products/${productId}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  // Get all users
  async getUsers(): Promise<any> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/users`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Get all users
  async registerUser(data: {
    firstName: string;
    lastName: string;
    name?: string;
    jobTitle?: string;
    email: string;
    phone: string;
    username: string;
    redirectUrl?: string;
    address: {
      street1: string;
      street2?: string;
      city: string;
      state: string;
      postalCode: string;
    };
  }): Promise<{ url: any }> {
    try {
      const response = await fetch(`${API_BASE_URL}/login/registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Registration failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error registering user:', error);
      throw error;
    }
  },

  // Request a password reset for the given username
  async forgotPassword(username: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/login/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Request failed. Please try again.');
    }
  },

  // Submit verification code and new password to complete a password reset
  async resetPassword(username: string, code: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/login/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, code, newPassword }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Request failed. Please try again.');
    }
  },

  // Get login metrics for an instance
  async getLoginMetrics(
    instanceId: string,
    params: { fromTime: string; toTime: string },
  ): Promise<LoginMetrics> {
    try {
      const query = new URLSearchParams({ fromTime: params.fromTime, toTime: params.toTime });
      const response = await authenticatedFetch(
        `${QB_API_BASE}/instances/${instanceId}/metrics/logins?${query}`,
      );
      return await response.json();
    } catch (error) {
      console.error('Error fetching login metrics:', error);
      throw error;
    }
  },

  // Get transactions by type for an instance
  async getTransactionsByType(
    instanceId: string,
    params: { fromTime: string; toTime: string; groupBy?: string },
  ): Promise<TransactionsByTypeEntry[]> {
    try {
      const query = new URLSearchParams({ fromTime: params.fromTime, toTime: params.toTime });
      if (params.groupBy) query.set('groupBy', params.groupBy);
      const response = await authenticatedFetch(
        `${QB_API_BASE}/instances/${instanceId}/metrics/transactionsByType?${query}`,
      );
      return await response.json();
    } catch (error) {
      console.error('Error fetching transactions by type:', error);
      throw error;
    }
  },

  // Get invoices for a customer
  async getInvoices(customerId: string): Promise<Invoice[]> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/invoices?customerId=${customerId}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  },

  // Get a single invoice by ID
  async getInvoiceById(invoiceId: string): Promise<Invoice> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/invoices/${invoiceId}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  },

  // Create checkout session
  async createCheckoutSession(data: {
    productId: string;
    priceId: string;
    quantity: number;
    trialDays: number;
    userId: string;
    customerId: string;
    name?: string;
    email?: string;
  }): Promise<{ url: any }> {
    try {
      const response = await authenticatedFetch(`${QB_API_BASE}/subscriptions/checkout`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },
};
